import { yupResolver } from "@hookform/resolvers/yup";
import type { ModalFuncProps, ModalProps } from "antd/lib/modal";
import clsx from "clsx";
import { isFunction } from "lodash";
import { ReactNode, useCallback } from "react";
import { DefaultValues, FieldValues, FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Merge } from "type-fest";
import * as yup from "yup";

import { MultistepProvider, Step, useMultistep } from "@sol/forms";
import { usePreventRouteChange } from "@sol/routing";
import { Drawer } from "@sol/uikit/feedback/Drawer/Drawer";
import { Modal } from "@sol/uikit/feedback/Modal/Modal";
import { Button } from "@sol/uikit/general/Button/Button";

import { ModalFormSteper } from "./ModalFormSteper";

export type ModalFormSteps<TFormValues extends FieldValues = FieldValues> = Merge<
    Step<TFormValues>,
    { stepTitle?: string }
>;

type ModalFormProps<TFormValues extends FieldValues = FieldValues> = ModalProps & {
    variant?: "modal" | "drawer";
    steps: ModalFormSteps<TFormValues>[];
    schema?: yup.ObjectSchema<any>;
    defaultValues?: DefaultValues<TFormValues>;
    children?: ReactNode | ((props: { currentStep: number }) => ReactNode);
    onSuccess?: () => void;
    onError?: () => void;
    onSubmit: (values: TFormValues) => Promise<any>;
    /** Called once the user closes the form without submitting it, with what they leave behind */
    onDiscard?: (draft: ModalFormDraft<TFormValues>) => void;
    submitLabel?: ReactNode;
};

export type ModalFormDraft<TFormValues extends FieldValues = FieldValues> = {
    stepIndex: number;
    stepCount: number;
    values: TFormValues;
};

export const ModalForm = <TFormValues extends Record<string, any>>({
    width = "80%",
    closable = true,
    footer = null,
    schema,
    steps,
    defaultValues,
    children,
    onSubmit,
    onCancel,
    onDiscard,
    onSuccess,
    onError,
    variant = "modal",
    submitLabel,
    ...props
}: ModalFormProps<TFormValues>) => {
    const [t] = useTranslation(undefined, { keyPrefix: "@sol" });
    const [modal, modalContextHolder] = Modal.useModal();

    const form = useForm<TFormValues>({
        mode: "onChange",
        ...(schema && { resolver: yupResolver(schema) }),
        defaultValues,
    });

    const {
        handleSubmit,
        formState: { isSubmitting },
    } = form;

    const isDirty = Object.keys(form.formState.dirtyFields).length > 0;

    const multistep = useMultistep({ steps, form });
    const isSingleStep = steps.length === 1;
    const currentStep = steps[multistep.stepIndex];
    const { force: allowRouteChange, reset: restoreRouteGuard } = usePreventRouteChange(isDirty);

    const confirmModal = ({ ...props }: ModalFuncProps) => {
        modal.confirm({
            okText: t("forms.ModalForm.warning.actions.confirm"),
            cancelText: t("forms.ModalForm.warning.actions.cancel"),
            centered: true,
            ...props,
        });
    };

    const handleFormSubmit = async (values: TFormValues) => {
        // Some callers navigate inside onSubmit after the API succeeds.
        allowRouteChange();
        try {
            const results = await onSubmit(values);

            if (!results) {
                if (results === false) {
                    restoreRouteGuard();
                }
                onError?.();
                return;
            }

            form.reset(defaultValues);
            onSuccess?.();

            return results;
        } catch {
            // Mutation errors are displayed by the caller. Keep the form and its navigation guard for retry.
            restoreRouteGuard();
            onError?.();
        }
    };

    const resetModalFormState = useCallback(() => {
        form.reset(defaultValues);
        form.trigger();
    }, [form, defaultValues]);

    // Handle modal close with confirmation if there are unsaved changes
    const handleModalCancel = useCallback(
        (e: React.MouseEvent<HTMLButtonElement>) => {
            const discard = () =>
                onDiscard?.({ stepIndex: multistep.stepIndex, stepCount: steps.length, values: form.getValues() });

            if (isDirty) {
                confirmModal({
                    title: t("forms.ModalForm.warning.unsavedChanges.title"),
                    content: t("forms.ModalForm.warning.unsavedChanges.message"),
                    onOk: () => {
                        discard();
                        allowRouteChange();
                        resetModalFormState();
                        multistep.jump(0);
                        onCancel?.(e);
                    },
                });
                return;
            }

            discard();
            resetModalFormState();
            onCancel?.(e);
        },
        [isDirty, onCancel, onDiscard, multistep.stepIndex, steps.length, form, allowRouteChange, t],
    );

    const renderModalWrapper = (children: ReactNode, stepTitle?: string) => {
        const title = stepTitle ?? props.title;
        const drawerWidth =
            width === "80%"
                ? "min(720px, 100vw)"
                : typeof width === "string" || typeof width === "number"
                  ? width
                  : "min(720px, 100vw)";

        return (
            <>
                {variant === "drawer" ? (
                    <Drawer
                        open={props.open}
                        width={drawerWidth}
                        closable={closable}
                        onClose={handleModalCancel}
                        title={title}
                        destroyOnHidden
                        classNames={{ body: "p-ant-lg!" }}
                    >
                        {children}
                    </Drawer>
                ) : (
                    <Modal
                        footer={footer}
                        width={width}
                        closable={closable}
                        onCancel={handleModalCancel}
                        centered
                        title={title}
                        destroyOnHidden
                        {...props}
                    >
                        {children}
                    </Modal>
                )}
                {modalContextHolder}
            </>
        );
    };

    if (!children && steps.length === 0) {
        throw new Error("No form configuration provided");
    }

    const submit = handleSubmit(handleFormSubmit);

    return renderModalWrapper(
        <FormProvider {...form}>
            <MultistepProvider {...multistep}>
                <form>
                    {isFunction(children) ? children({ currentStep: multistep.stepIndex }) : children}
                    <div className="flex items-center justify-between">
                        {!isSingleStep && !multistep.isFirstStep && (
                            <Button
                                size="large"
                                onClick={() => multistep.jump(multistep.prev)}
                                data-cy="modal-form-prev"
                            >
                                {t("forms.ModalForm.control.prev")}
                            </Button>
                        )}
                        <div
                            className={clsx("mt-6 ml-auto flex items-center justify-center gap-6", {
                                "ml-auto": !isSingleStep,
                            })}
                        >
                            {!isSingleStep && (
                                <ModalFormSteper
                                    stepLabel={currentStep.label}
                                    currentStep={multistep.stepIndex}
                                    totalSteps={steps.length}
                                />
                            )}
                            {!isSingleStep && !multistep.isLastStep ? (
                                <Button
                                    type="primary"
                                    loading={isSubmitting}
                                    onClick={e => {
                                        e.preventDefault();
                                        multistep.jump(multistep.next);
                                    }}
                                    htmlType="button"
                                    size="large"
                                    disabled={!multistep.step.valid}
                                    data-cy="modal-form-next"
                                >
                                    {t("forms.ModalForm.control.next")}
                                </Button>
                            ) : (
                                <Button
                                    type="primary"
                                    loading={isSubmitting}
                                    htmlType="button"
                                    size="large"
                                    disabled={!multistep.step.valid}
                                    onClick={() => {
                                        submit();
                                    }}
                                    data-cy="modal-form-submit"
                                >
                                    {submitLabel ?? t("forms.ModalForm.control.submit")}
                                </Button>
                            )}
                        </div>
                    </div>
                </form>
            </MultistepProvider>
        </FormProvider>,
        isSingleStep ? currentStep.label : currentStep.stepTitle,
    );
};
